import { A, createAsync, revalidate } from "@solidjs/router";
import { Show, For, createSignal, onMount, createEffect, createMemo, Switch, Match } from "solid-js";

import { formatApprovedBodyId, formatLongDate, schemeData, SubscriptionState } from "~/lib";
import type { ApprovedBody } from "~/lib/types";
import { getByUserQuery, getAdminApprovedBodies } from "~/lib/queries";
import { queryByPK } from "~/_server/dynamoDB";
import { ADMIN } from "~/_server/constants";
import { approveNewApprovedBody, assignExistingApprovedBody, requireApprovedBody, suspendSubscription, terminateSubscription } from "~/lib/businessLogic";
import { AddApprovedBody } from "./AddApprovedBody";
import { useRIASApp } from "~/_context/UserProvider";

import { House, Building, Factory, GraduationCap, Ban, CirclePause, CircleArrowRight } from "lucide-solid";

import "~/_assets/select.css";

const icons = {
	House,
	Building,
	Factory,
	GraduationCap
};

// The statuses SubscriptionActions has a message for; keep in step with its Switch.
const ACTION_PANEL_STATUSES = [
	SubscriptionState.ASSESSING,
	SubscriptionState.PRACTICAL,
	SubscriptionState.REQUIRES_BODY,
	SubscriptionState.PENDING_BODY,
	SubscriptionState.SUSPENDED,
	SubscriptionState.RENEWING,
	SubscriptionState.PENDING,
	SubscriptionState.EXPIRED,
];

const hasActionPanel = ( status: string ) => ACTION_PANEL_STATUSES.includes( status as SubscriptionState );

export const Subscription = props => {
	const app = useRIASApp();
	const [subscription, setSubscription] = createSignal( props.subscription );
	const [approvedBody, setApprovedBody] = createSignal<ApprovedBody>();

	onMount( async () => {
		if ( !props.subscription && props.subscriptionId ) {
			const s = await getByUserQuery( 'SCHEME', props.scheme.id );

			setSubscription( s.SCHEME );
		}
	} );

	createEffect( async () => {
		if ( subscription().approvedBodyId ) {
			const ab = await queryByPK( `APPROVEDBODY#${subscription().approvedBodyId}`, '' );

			if ( ab && ab.length > 0 ) {
				setApprovedBody( ab[0] as ApprovedBody );
			}
		}
	} );

	const scheme = () => schemeData( subscription().scheme );

	const Icon = icons[scheme()?.icon ?? "House"] || icons.House;

	return (
		<>
			<Show when={!props.longForm}>
				<div class="grid grid-cols-2 gap-3" onClick={props.onClick} classList={props.classList}>
					<span>{schemeData( subscription().scheme )?.name ?? subscription().scheme}</span>
					<span>{subscription().name}</span>
					<span class="col-span-2">{approvedBody()?.address?.title ?? '-'}</span>
					<span class="col-span-2 text-xs mt-1.5">Renews {formatLongDate( subscription().expires )}</span>
				</div>
			</Show>

			<Show when={props.longForm}>
				<Show when={subscription().status == SubscriptionState.APPLYING}>
					<div class="text-center bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative" role="alert">
						<p>Your application for membership is being validated. This process can take up to 10 working days. You will receive an email as soon as the process is complete.</p>
					</div>
				</Show>

				<Show when={subscription().status != SubscriptionState.APPLYING}>
					<div class="h-auto border-l-16 grow" classList={{ [`border-sub-status-${subscription().status}`]: true }}>
						<div class="p-5 md:p-8">
							<div class="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
								{/*<!-- LEFT -->*/}
								<div class="flex gap-4">
									<div class="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl" classList={{ [`sub-status-${subscription().status}`]: true }}>
										<Icon class="h-8 w-8" />
									</div>

									<div>
										<div class="flex flex-wrap items-center gap-3">
											<h2 class="text-2xl font-semibold text-zinc-900">
												{scheme()?.name}
											</h2>

											{/*}
											<span class="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700">
												Active Membership
											</span>*/}
										</div>

										<p class="mt-2 max-w-3xl text-base leading-relaxed text-zinc-600">
											{scheme()?.description}
										</p>
									</div>
								</div>

							</div>

							<div class="mt-8 grid gap-6 border-y border-zinc-200 py-6 sm:grid-cols-2 md:grid-cols-3">
								<div>
									<div class="text-sm font-medium text-zinc-500">Certifier ID</div>

									<div class="mt-2 flex items-center gap-2">
										<span class="text-2xl font-semibold tracking-tight text-zinc-900">
											{subscription().name}
										</span>
									</div>
								</div>

								<div>
									<div class="text-sm font-medium text-zinc-500">Status</div>
									<div class="mt-3">
										<span class="inline-flex rounded-full px-3 py-1 text-sm font-medium" classList={{ [`sub-status-${subscription().status}`]: true }}>
											{subscription().status}
										</span>
									</div>
								</div>

								<Show when={subscription().status != SubscriptionState.CPD}>
									<div>
										<div class="text-sm font-medium text-zinc-500">Renewal Date</div>

										<div class="mt-2 text-2xl font-semibold tracking-tight text-zinc-900">
											<Show when={subscription().expires}>{formatLongDate( subscription().expires )}</Show>
											<Show when={!subscription().expires}>-</Show>
										</div>
									</div>
								</Show>
							</div>

							<Show when={subscription().status != SubscriptionState.CPD}>
								<div class="py-6">
									<div class="text-sm font-medium text-zinc-500">Approved Body</div>

									<div class="mt-2">
										<Switch fallback={<span class="text-2xl font-semibold tracking-tight text-zinc-900">-</span>}>
											<Match when={subscription().pendingAB}>
												{pending => (
													<span class="text-2xl font-semibold tracking-tight text-zinc-900">
														Pending approval of {pending().address?.title ?? pending().address?.name}
													</span>
												)}
											</Match>

											<Match when={approvedBody()}>
												{ab => (
													<>
														<div class="flex flex-wrap items-baseline gap-x-3">
															<span class="text-2xl font-semibold tracking-tight text-zinc-900">
																{ab().name ?? ab().address?.title ?? ab().address?.name}
															</span>
															<span class="text-sm font-medium text-zinc-500">
																{formatApprovedBodyId( subscription().scheme, subscription().approvedBodyId )}
															</span>
														</div>

														<dl class="mt-2 space-y-0.5 text-sm">
															<div><dt class="inline text-zinc-500">Certification Co-ordinator: </dt><dd class="inline">{[ab().coordinator?.name, ab().coordinator?.email, ab().coordinator?.telephone].filter( Boolean ).join( ' – ' ) || '-'}</dd></div>

															<Show when={ab().deputy?.name || ab().deputy?.email}>
																<div><dt class="inline text-zinc-500">Deputy Co-ordinator: </dt><dd class="inline">{[ab().deputy?.name, ab().deputy?.email, ab().deputy?.telephone].filter( Boolean ).join( ' – ' )}</dd></div>
															</Show>
														</dl>
													</>
												)}
											</Match>
										</Switch>
									</div>
								</div>
							</Show>
						</div>
					</div>

					{/* Only takes space when there is something to say - an active sub has no message. */}
					<Show when={hasActionPanel( subscription().status )}>
						<div class="xl:w-125 flex justify-center items-center px-5 pb-5 md:px-8 xl:p-0">
							<SubscriptionActions subscription={subscription()} isAdmin={app.userIs( ADMIN )} />
						</div>
					</Show>

					<Show when={app.userIs( ADMIN )}>
						<SubscriptionAdminActions subscription={subscription()} />
					</Show>
				</Show>
			</Show>
		</>
	);
}


const ApproveApprovedBody = ( props ) => {
	const bodies = createAsync( () => getAdminApprovedBodies() );

	const [selectedId, setSelectedId] = createSignal( '' );
	const [submitting, setSubmitting] = createSignal( false );
	const [error, setError] = createSignal( '' );

	const pending = () => props.subscription.pendingAB;

	const handleCreateNew = async () => {
		if ( !confirm( 'Create a new approved body from the submitted details and assign it to this subscription?' ) ) return;

		setSubmitting( true );
		setError( '' );

		try {
			await approveNewApprovedBody( props.subscription );
			await revalidate( 'getCertifierSubscriptions' );
		} catch ( err ) {
			console.error( 'Error creating approved body', err );
			setError( 'There was an error creating the approved body. Please try again.' );
		} finally {
			setSubmitting( false );
		}
	};

	const handleAssignExisting = async () => {
		if ( !selectedId() ) {
			setError( 'Choose an approved body to assign' );
			return;
		}

		setSubmitting( true );
		setError( '' );

		try {
			await assignExistingApprovedBody( props.subscription, selectedId() );
			await revalidate( 'getCertifierSubscriptions' );
		} catch ( err ) {
			console.error( 'Error assigning approved body', err );
			setError( 'There was an error assigning the approved body. Please try again.' );
		} finally {
			setSubmitting( false );
		}
	};

	return (
		<div class="w-full rounded-lg border border-lime-300 bg-lime-50 p-4 text-left">
			<h3 class="text-lg font-bold text-zinc-900">Approve Submitted Body</h3>

			<dl class="mt-2 space-y-0.5 text-sm">
				<div><dt class="inline text-zinc-500">Name: </dt><dd class="inline">{pending()?.address?.title ?? pending()?.address?.name ?? '-'}</dd></div>

				<div><dt class="inline text-zinc-500">Address: </dt><dd class="inline">{[pending()?.address?.addr1, pending()?.address?.addr2, pending()?.address?.town, pending()?.address?.county, pending()?.address?.postcode].filter( Boolean ).join( ', ' ) || '-'}</dd></div>

				<div><dt class="inline text-zinc-500">Coordinator: </dt><dd class="inline">{[pending()?.coordinator?.name, pending()?.coordinator?.email, pending()?.coordinator?.telephone].filter( Boolean ).join( ' – ' ) || '-'}</dd></div>

				<Show when={pending()?.deputy?.name || pending()?.deputy?.email}>
					<div><dt class="inline text-zinc-500">Deputy: </dt><dd class="inline">{[pending()?.deputy?.name, pending()?.deputy?.email, pending()?.deputy?.telephone].filter( Boolean ).join( ' – ' )}</dd></div>
				</Show>
			</dl>

			<div class="mt-4 flex flex-col gap-2">
				<button
					type="button"
					disabled={submitting()}
					class="rias bg-lime-600 text-white hover:bg-lime-700 disabled:opacity-50 w-full"
					onClick={handleCreateNew}>
					Create New Body
				</button>

				<div class="my-1 flex items-center gap-2 text-xs text-zinc-500">
					<span class="h-px flex-1 bg-lime-300" /> or assign an existing one <span class="h-px flex-1 bg-lime-300" />
				</div>

				<select
					class="rias-select w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
					value={selectedId()}
					onChange={e => setSelectedId( e.currentTarget.value )}>
					<option value="">Select approved body…</option>
					<For each={bodies()}>
						{(ab: any) => <option value={ab.id}>{ab.name ?? ab.address?.title ?? ab.address?.name ?? 'Unnamed'}</option>}
					</For>
				</select>

				<button
					type="button"
					disabled={submitting() || !selectedId()}
					class="rias bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 w-full"
					onClick={handleAssignExisting}>
					Assign Selected
				</button>
			</div>

			<Show when={error()}>
				<p class="mt-2 text-sm text-red-600">{error()}</p>
			</Show>
		</div>
	);
};

// Lifecycle is a strict one-way pipeline: suspend first, then terminate - so a
// single control does double duty, switching action based on current status.
// This is an admin action taken maybe once a year per subscription, so it's kept
// as a quiet icon that only reveals its label (and turns into a full button) on hover.
// During the practical stage it instead offers the move on to Approved Body submission.
const SubscriptionAdminActions = ( props ) => {
	const [sub] = createSignal( props.subscription );
	const [running, setRunning] = createSignal( false );
	const [error, setError] = createSignal<string | null>( null );

	const action = () => {
		if ( sub().status == SubscriptionState.ACTIVE ) return 'suspend';
		if ( sub().status == SubscriptionState.SUSPENDED ) return 'terminate';
		if ( sub().status == SubscriptionState.PRACTICAL ) return 'require-body';
		return null;
	};

	const labels = {
		'suspend': 'Suspend Subscription',
		'terminate': 'Terminate Subscription',
		'require-body': 'Move to Approved Body Submission',
	};

	const handleClick = async () => {
		const act = action();

		if ( !act ) return;

		const confirmMessage = {
			'suspend': "Suspend this subscription? The certifier will not be able to issue certificates until it is reinstated.",
			'terminate': "Terminate this subscription? This cannot be undone.",
			'require-body': "Move this certifier on to Approved Body submission? They will be asked to submit their Approved Body.",
		}[act];

		if ( !confirm( confirmMessage ) ) return;

		setRunning( true );
		setError( null );

		try {
			if ( act === 'suspend' ) {
				await suspendSubscription( sub() );
			} else if ( act === 'require-body' ) {
				await requireApprovedBody( sub() );
			} else {
				await terminateSubscription( sub() );
			}

			await revalidate( 'getCertifierSubscriptions' );
		} catch ( err ) {
			console.error( 'Error updating subscription lifecycle', err );
			setError( 'Action failed. Please try again.' );
		} finally {
			setRunning( false );
		}
	};

	return (
		<Show when={action()}>
			<div class="self-end xl:self-center shrink-0 px-5 pb-5 md:px-8 xl:p-0 xl:px-4">
				<button
					type="button"
					disabled={running()}
					onClick={handleClick}
					title={labels[action()!]}
					class="group flex items-center gap-2 overflow-hidden rounded-full border px-2 py-2 text-xs font-medium transition-all duration-200 disabled:opacity-50"
					classList={{
						"border-amber-300 text-amber-700 hover:bg-amber-50": action() === 'suspend',
						"border-red-300 text-red-700 hover:bg-red-50": action() === 'terminate',
						"border-lime-400 text-lime-700 hover:bg-lime-50": action() === 'require-body',
					}}>
					<Switch>
						<Match when={action() === 'suspend'}><CirclePause class="h-4 w-4 shrink-0" /></Match>
						<Match when={action() === 'terminate'}><Ban class="h-4 w-4 shrink-0" /></Match>
						<Match when={action() === 'require-body'}><CircleArrowRight class="h-4 w-4 shrink-0" /></Match>
					</Switch>
					<span class="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:max-w-56 group-hover:opacity-100">
						{labels[action()!]}
					</span>
				</button>

				<Show when={error()}>
					<p class="mt-1 max-w-32 text-right text-xs text-red-600">{error()}</p>
				</Show>
			</div>
		</Show>
	);
}

const SubscriptionActions = ( props ) => {
	const [sub] = createSignal( props.subscription );

	return (
		<div class="w-[90%] my-4">
			<Switch>
				<Match when={sub().status == SubscriptionState.ASSESSING}>
					<div class="text-center bg-purple-100 border border-purple-400 text-purple-700 px-4 py-3 rounded-lg relative" role="alert">
						<h3 class="text-lg font-bold">Over to you!</h3>

						{/* CPD has no competency tests to pass: the materials and assessments are there to use. */}
						<Show
							when={schemeData( sub().scheme )?.notScheme}
							fallback={<p class="my-2">Your next step is to complete the membership assessments. <A href="/dashboard/exams">Click here to begin</A>.</p>}>
							<p class="my-2">You can access all of the learning materials via the <A href="/dashboard/logbook">Log Book</A> tab. You are free to take the <A href="/dashboard/exams">assessments</A> as many times as you wish.</p>
						</Show>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.PRACTICAL} >
					<div class="text-center bg-purple-100 border border-purple-400 text-purple-700 px-4 py-3 rounded-lg relative" role="alert">
						<Switch>
							<Match when={sub().scheme == "section-6-non-domestic" || sub().scheme == "section-6-non-domestic-advanced"}>

								<h3 class="text-lg font-bold">Software Test</h3>

								<p class="my-2">The next step towards becoming an Approved Certifier of Design is to either:</p>

								<ul class="list-disc list-inside text-left text-sm">
									<li>
										provide <a href="mailto:rias-regs@rias.org.uk">rias-regs@rias.org.uk</a> with details of an accredited training course which you have completed.
										(Details can be found at
										<a href="https://www.gov.scot/publications/building-standards-approved-energy-assessment-software-guidance/">
											the Scottish Government's website
										</a>).
									</li>
									<li>
										or, undertake the training and practical test provided by Strathclyde
										University which is{" "}
										<a href="https://onlineshop.strath.ac.uk/product-catalogue/engineering-faculty/mechanical-aerospace-engineering/courses/riascibse-certifier-of-design-training">
											available here
										</a>. The university will inform RIAS-regs when you have
										satisfactorily completed their training and tests.
									</li>
								</ul>
							</Match>

							<Match when={sub().scheme == "section-6-domestic"}>
								<h3 class="text-lg font-bold">Practical SAP Test</h3>
								<p>This will be sent to you separately by <a href="https://home-data.co.uk/">HEADs</a>, which administers this part of the competency process.</p>
							</Match>
						</Switch>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.REQUIRES_BODY}>
					<div class="text-center bg-lime-100 border border-lime-400 text-lime-700 px-4 py-3 rounded relative" role="alert">
						<h3 class="text-lg font-bold">Your Approved Body</h3>

						<p class="my-2">Your next step is to submit an approved body.</p>

						<AddApprovedBody subscription={sub()} />	
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.PENDING_BODY}>
					<Show
						when={props.isAdmin}
						fallback={
							<div class="text-center bg-lime-100 border border-lime-400 text-lime-700 px-4 py-3 rounded relative" role="alert">
								<h3 class="text-lg font-bold">Thank you for submitting your Approved Body</h3>

								<p class="my-2">Verification is underway, and we will be in touch shortly.</p>
							</div>
						}>
						<ApproveApprovedBody subscription={sub()} />
					</Show>
				</Match>

				<Match when={sub().status == SubscriptionState.SUSPENDED} >
					<div class="text-center bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
						<h3 class="text-lg font-bold">Subscription Suspended</h3>

						<p class="my-3">Your subscription has been suspended. If you have not received an email regarding this matter, please <a href="mailto:support@rias-regs.co.uk">contact Support</a>.</p>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.RENEWING} >
					<div class="text-center bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
						<h3 class="text-lg font-bold">Subscription Renewing</h3>

						<p class="my-3">Your subscription has expired, but can be renewed by paying the renewal fee. If you have not received an email regarding renewal, please <a href="mailto:support@rias-regs.co.uk">contact Support</a>.</p>

						<p class="my-3">You must not issue certificates until your subscription is renewed.</p>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.PENDING} >
					<div class="text-center bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
						<Switch>
							<Match when={sub().scheme.toLowerCase() == "cpd"} >
								<h3 class="text-lg font-bold">Your Joining Fee is due</h3>

								<p class="my-3">Your details have now been verified and you are invited to join the CPD scheme. You must pay your joining fee to access the learning materials and
									online competency tests.</p>
							</Match>
							<Match when={sub().scheme.toLowerCase() != "cpd"} >
								<h3 class="text-lg font-bold">Your Joining and Subscription Fees are due</h3>

								<p class="my-3">Your details have now been verified and you are invited to join the scheme. You must pay your joining fee and first year's subscription to access the
									online competency tests.</p>
							</Match>
						</Switch>
						<p class="my-3">Invoices can be found on your <a href="/dashboard/account" class="italic font-bold">account page</a>.</p>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.EXPIRED} >
					<div class="text-center bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
						<h3 class="text-lg font-bold">Subscription Expired</h3>

						<p class="my-3">Your subscription has expired. If you have not received an email regarding renewal, please <a href="mailto:support@rias-regs.co.uk">contact Support</a>.</p>

						<p class="my-3">You may no longer issue certificates.</p>
					</div>
				</Match>
			</Switch>
		</div>
	);

}